import React from 'react'
import { Play, Pause, Download, Share, Edit3, Trash2, Clock, User, Globe } from 'lucide-react'

function AudioCard({ 
  audio, 
  isPlaying, 
  onPlay, 
  onDownload, 
  onShare, 
  onEdit, 
  onDelete 
}) {
  const formatDuration = (duration) => {
    if (!duration) return '0:00'
    const minutes = Math.floor(duration / 60)
    const seconds = Math.floor(duration % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }
  
  return (
    <div className="card group hover:shadow-xl transition-all duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate mb-1">
            {audio.title}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {audio.text}
          </p>
        </div>
        
        {/* Play Button */}
        <button
          onClick={onPlay}
          className="ml-3 p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full transition-colors duration-200 flex-shrink-0"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
      </div>
      
      {/* Audio Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{formatDuration(audio.duration)}</span>
          </div>
          <div className="flex items-center gap-1">
            <User className="w-4 h-4" />
            <span className="capitalize">{audio.voiceSettings?.gender || 'Unknown'}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Globe className="w-4 h-4" />
            <span>{audio.voiceSettings?.accent || 'en-US'}</span>
          </div>
          <span>•</span>
          <span>{formatDate(audio.createdAt)}</span>
        </div>
      </div>
      
      {/* Voice Settings Preview */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <div className="font-medium text-gray-900">{audio.voiceSettings?.speed?.toFixed(1)}x</div>
            <div className="text-gray-600">Speed</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-gray-900">{audio.voiceSettings?.pitch?.toFixed(1)}</div>
            <div className="text-gray-600">Pitch</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-gray-900">{Math.round((audio.voiceSettings?.volume || 1) * 100)}%</div>
            <div className="text-gray-600">Volume</div>
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={onDownload}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
          
          <button
            onClick={onShare}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200"
            title="Share"
          >
            <Share className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          
          <button
            onClick={onDelete}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Playing Indicator */}
      {isPlaying && (
        <div className="absolute top-4 right-4 flex items-center gap-1">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-primary-500 rounded wave-animation"
              style={{ height: Math.random() * 12 + 8 }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default AudioCard
